import { useInteriorDesigner } from "../context/InteriorDesignerContext";
import {
  findRemoveFurnitureChild,
  getActiveNode,
  isFurnitureToggleEnabled,
  isFurnitureToggleVisible,
} from "../context/selectors";
import { useHistoryActions } from "./useHistoryActions";

/**
 * Shared furniture-toggle state and handler, used by both the desktop overlay
 * controls and the compact controls so the (non-trivial) toggle logic lives in
 * one place.
 */
export function useFurnitureToggle() {
  const { state, dispatch } = useInteriorDesigner();
  const { navigateToNode } = useHistoryActions();

  const isGenerating = state.generation.status === "loading";
  const isVisible = isFurnitureToggleVisible({ state });
  const canToggle = isFurnitureToggleEnabled({ state });
  const furnitureVisible = state.furnitureVisible;

  const toggle = () => {
    if (isGenerating) {
      return;
    }

    if (state.furnitureVisible) {
      const child = findRemoveFurnitureChild({ state });
      if (child) {
        dispatch({ type: "FURNITURE_TOGGLED" });
        dispatch({ type: "NODE_NAVIGATED", nodeId: child.id });
        navigateToNode({ photoId: child.id }).catch(() => {});
        return;
      }
      dispatch({ type: "FURNITURE_TOGGLED" });
      dispatch({
        type: "GENERATION_REQUESTED",
        actions: [{ type: "remove_furniture" }],
      });

      return;
    }

    const activeNode = getActiveNode({ state });
    if (!activeNode?.parent_photo_id) {
      return;
    }

    dispatch({ type: "FURNITURE_TOGGLED" });
    dispatch({
      type: "NODE_NAVIGATED",
      nodeId: activeNode.parent_photo_id,
    });

    navigateToNode({ photoId: activeNode.parent_photo_id }).catch(() => {});
  };

  return {
    /**
     * Whether the furniture toggle should be shown at all
     */
    isVisible,
    /**
     * Whether the toggle can currently be changed
     */
    canToggle,
    /**
     * Whether furniture is currently shown in the active image
     */
    furnitureVisible,
    /**
     * Whether a generation is in progress (toggle is locked)
     */
    isGenerating,
    /**
     * Toggles furniture visibility
     */
    toggle,
  };
}
